import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Purchase Management Page (Level 5.3)
 *
 * Admin page for managing purchases from suppliers
 * Records what we buy, tracks costs, updates inventory
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiClient from '../../utils/apiClient';
import { notify } from '../../utils/notifications';
import { PURCHASE_TYPE_LABELS, PAYMENT_STATUS_LABELS, PURCHASE_TYPE_COLORS, PAYMENT_STATUS_COLORS } from '../../types/purchase';
import BaseModal from '../../components/shared/BaseModal';
import { RecordPaymentModal } from '../../components/RecordPaymentModal';
import TabPageLayout from './FinancialHub/TabPageLayout';
import { Button, StatsGrid, StatCard, StatIcon, StatLabel, StatValue, TableContainer, Table, Th, Td, FiltersContainer, FilterGroup, Label, Input, Select, EmptyState, EmptyStateIcon, EmptyStateText, SortableTh } from './FinancialHub/SharedComponents';
import { FaShoppingCart, FaDollarSign, FaCheckCircle, FaClock } from 'react-icons/fa';
const PurchaseManagement = () => {
    const [purchases, setPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [statistics, setStatistics] = useState(null);
    // Payment modal state
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentPurchase, setPaymentPurchase] = useState(null);
    // Filters
    const [supplierFilter, setSupplierFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    // Sort state
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    // Form state
    const [formData, setFormData] = useState({
        supplierId: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        purchaseType: 'outright',
        items: [],
        taxRate: 0, // Tax as percentage (e.g., 8.5 for 8.5%)
        taxAmount: 0, // Calculated: subtotal * (taxRate / 100)
        shippingCost: 0,
        discountAmount: 0,
        notes: '',
        dueDate: ''
    });
    // Line item form (for adding diamonds to purchase)
    const [lineItem, setLineItem] = useState({
        diamondId: '',
        stockNumber: '',
        description: '',
        quantity: 1,
        costPerCarat: 0,
        caratWeight: 0,
        totalCost: 0
    });
    // Fetch purchases
    const fetchPurchases = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (supplierFilter)
                params.append('supplierId', supplierFilter);
            if (statusFilter)
                params.append('paymentStatus', statusFilter);
            if (typeFilter)
                params.append('purchaseType', typeFilter);
            const queryString = params.toString();
            const url = `/purchases-list${queryString ? '?' + queryString : ''}`;
            const response = await apiClient.get(url);
            if (response.ok && response.data) {
                setPurchases(response.data.purchases || []);
                setStatistics(response.data.statistics);
            }
            else {
                notify.error('Error', response.error || 'Failed to load purchases');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to fetch purchases');
        }
        finally {
            setLoading(false);
        }
    };
    // Fetch suppliers for dropdown
    const fetchSuppliers = async () => {
        try {
            const response = await apiClient.get('/suppliers-list?status=active');
            if (response.ok && response.data) {
                setSuppliers(response.data.suppliers || []);
            }
        }
        catch (err) {
            console.error('Failed to fetch suppliers:', err);
        }
    };
    // Create purchase
    const createPurchase = async () => {
        setLoading(true);
        if (formData.items.length === 0) {
            notify.error('Error', 'Add at least one line item');
            setLoading(false);
            return;
        }
        try {
            // Calculate tax amount from percentage before sending
            const taxAmount = calculateTaxAmount();
            const purchaseData = {
                ...formData,
                taxAmount: taxAmount // Override with calculated amount
            };
            const response = await apiClient.post('/purchases-create', purchaseData);
            if (response.ok && response.data) {
                notify.success('Success', `Purchase created: ${response.data.purchaseNumber}`);
                setShowAddModal(false);
                resetForm();
                fetchPurchases();
            }
            else {
                notify.error('Error', response.error || 'Failed to create purchase');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to create purchase');
        }
        finally {
            setLoading(false);
        }
    };
    // Lookup diamond by stock number
    const lookupDiamond = async (stockNumber) => {
        if (!stockNumber.trim())
            return;
        // Prevent multiple lookups for same stock number
        if (lineItem.stockNumber === stockNumber && lineItem.diamondId) {
            return; // Already looked up
        }
        try {
            // Search diamonds by stock number using search parameter
            const response = await apiClient.get(`/diamonds-inventory?search=${encodeURIComponent(stockNumber)}&limit=10`);
            console.log('💎 Diamond Lookup Response:', {
                ok: response.ok,
                hasData: !!response.data,
                hasDataData: !!response.data?.data,
                hasDiamonds: !!response.data?.data?.diamonds,
                count: response.data?.data?.diamonds?.length,
                fullResponse: response.data
            });
            // API returns: { success, data: { diamonds: [...] } }
            const diamonds = response.data?.data?.diamonds;
            if (response.ok && diamonds && diamonds.length > 0) {
                // Find exact match by stockNumber (case-insensitive)
                const diamond = diamonds.find((d) => d.stockNumber?.toLowerCase() === stockNumber.toLowerCase()) || diamonds[0];
                console.log('💎 Found Diamond:', diamond);
                // Auto-fill line item form
                // Use costPerCarat if available, otherwise use pricePerCarat as starting point
                const suggestedCost = diamond.costPerCarat || diamond.pricePerCarat || 0;
                const newLineItem = {
                    diamondId: diamond.id,
                    stockNumber: diamond.stockNumber,
                    description: `${diamond.caratWeight}ct ${diamond.shape} ${diamond.color || 'Fancy'} ${diamond.clarity}`,
                    quantity: 1,
                    caratWeight: diamond.caratWeight,
                    costPerCarat: suggestedCost, // Pre-fill with cost or retail price
                    totalCost: 0 // Will be calculated
                };
                console.log('💎 Setting LineItem:', newLineItem);
                console.log('💎 Diamond prices:', {
                    costPerCarat: diamond.costPerCarat,
                    pricePerCarat: diamond.pricePerCarat,
                    suggestedCost: suggestedCost
                });
                setLineItem(newLineItem);
                const priceNote = diamond.costPerCarat
                    ? `Cost: $${diamond.costPerCarat.toLocaleString()}/ct`
                    : `Retail: $${diamond.pricePerCarat?.toLocaleString() || 0}/ct (adjust to actual cost)`;
                notify.success('Success', `Found: ${diamond.stockNumber} - ${diamond.caratWeight}ct ${diamond.shape} | ${priceNote}`);
            }
            else {
                console.log('💎 Diamond not found, continuing with manual entry');
                // Not found - keep stock number but clear other fields
            }
        }
        catch (err) {
            console.error('💎 Diamond lookup error:', err);
            // Continue - not a critical error
        }
    };
    // Add line item to purchase
    const addLineItem = () => {
        if (!lineItem.stockNumber || lineItem.costPerCarat <= 0 || lineItem.caratWeight <= 0) {
            notify.error('Error', 'Complete line item details required');
            return;
        }
        const totalCost = lineItem.costPerCarat * lineItem.caratWeight * lineItem.quantity;
        const newItem = {
            ...lineItem,
            totalCost,
            id: `temp-${Date.now()}`
        };
        setFormData({
            ...formData,
            items: [...formData.items, newItem]
        });
        // Reset line item form
        setLineItem({
            diamondId: '',
            stockNumber: '',
            description: '',
            quantity: 1,
            costPerCarat: 0,
            caratWeight: 0,
            totalCost: 0
        });
        notify.success('Success', 'Line item added');
    };
    // Remove line item
    const removeLineItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: newItems });
    };
    // Calculate subtotal
    const calculateSubtotal = () => {
        return formData.items.reduce((sum, item) => sum + item.totalCost, 0);
    };
    // Calculate tax amount from percentage
    const calculateTaxAmount = () => {
        const subtotal = calculateSubtotal();
        return (subtotal * formData.taxRate) / 100;
    };
    // Calculate total
    const calculateTotal = () => {
        const subtotal = calculateSubtotal();
        const taxAmount = calculateTaxAmount();
        return subtotal + taxAmount + formData.shippingCost - formData.discountAmount;
    };
    // View purchase details
    const viewPurchase = async (purchaseId) => {
        setLoading(true);
        try {
            const response = await apiClient.get(`/purchases-get?id=${purchaseId}`);
            if (response.ok && response.data) {
                setSelectedPurchase(response.data.purchase);
            }
            else {
                notify.error('Error', response.error || 'Failed to load purchase');
            }
        }
        catch (err) {
            notify.error('Error', err.message || 'Failed to load purchase');
        }
        finally {
            setLoading(false);
        }
    };
    // Record payment handler
    const handleRecordPayment = (purchase) => {
        setPaymentPurchase(purchase);
        setShowPaymentModal(true);
    };
    const handlePaymentSuccess = () => {
        fetchPurchases();
        setShowPaymentModal(false);
        setPaymentPurchase(null);
    };
    // Reset form
    const resetForm = () => {
        setFormData({
            supplierId: '',
            purchaseDate: new Date().toISOString().split('T')[0],
            purchaseType: 'outright',
            items: [],
            taxRate: 0,
            taxAmount: 0,
            shippingCost: 0,
            discountAmount: 0,
            notes: '',
            dueDate: ''
        });
    };
    // Load data on mount
    useEffect(() => {
        fetchPurchases();
        fetchSuppliers();
    }, []);
    // Handle sort
    const handleSort = (key) => {
        if (sortKey === key) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            }
            else if (sortDirection === 'desc') {
                setSortDirection(null);
                setSortKey(null);
            }
            else {
                setSortDirection('asc');
            }
        }
        else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };
    // Sort purchases
    const sortPurchases = (purchasesToSort) => {
        if (!sortKey || !sortDirection)
            return purchasesToSort;
        return [...purchasesToSort].sort((a, b) => {
            let aValue;
            let bValue;
            switch (sortKey) {
                case 'purchaseNumber':
                    aValue = a.purchaseNumber?.toLowerCase() || '';
                    bValue = b.purchaseNumber?.toLowerCase() || '';
                    break;
                case 'purchaseDate':
                    aValue = new Date(a.purchaseDate).getTime();
                    bValue = new Date(b.purchaseDate).getTime();
                    break;
                case 'supplier':
                    aValue = a.supplierName?.toLowerCase() || '';
                    bValue = b.supplierName?.toLowerCase() || '';
                    break;
                case 'purchaseType':
                    aValue = a.purchaseType || '';
                    bValue = b.purchaseType || '';
                    break;
                case 'items':
                    aValue = a.items?.length || 0;
                    bValue = b.items?.length || 0;
                    break;
                case 'total':
                    aValue = a.total || 0;
                    bValue = b.total || 0;
                    break;
                case 'amountPaid':
                    aValue = a.amountPaid || 0;
                    bValue = b.amountPaid || 0;
                    break;
                case 'amountOwed':
                    aValue = a.amountOwed || 0;
                    bValue = b.amountOwed || 0;
                    break;
                case 'paymentStatus':
                    aValue = a.paymentStatus || '';
                    bValue = b.paymentStatus || '';
                    break;
                default:
                    return 0;
            }
            if (aValue < bValue)
                return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue)
                return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    };
    // Get sorted purchases
    const sortedPurchases = sortPurchases(purchases);
    // Stats Section
    const statsSection = statistics && (_jsxs(StatsGrid, { children: [_jsxs(StatCard, { color: "#3b82f6", children: [_jsx(StatIcon, { color: "#3b82f6", children: _jsx(FaShoppingCart, {}) }), _jsx(StatLabel, { children: "Total Purchases" }), _jsx(StatValue, { children: statistics.total })] }), _jsxs(StatCard, { color: "#10b981", children: [_jsx(StatIcon, { color: "#10b981", children: _jsx(FaCheckCircle, {}) }), _jsx(StatLabel, { children: "Paid" }), _jsx(StatValue, { children: statistics.byStatus?.paid || 0 })] }), _jsxs(StatCard, { color: "#8b5cf6", children: [_jsx(StatIcon, { color: "#8b5cf6", children: _jsx(FaDollarSign, {}) }), _jsx(StatLabel, { children: "Total Value" }), _jsxs(StatValue, { children: ["$", statistics.totalValue?.toLocaleString() || 0] })] }), _jsxs(StatCard, { color: "#ef4444", children: [_jsx(StatIcon, { color: "#ef4444", children: _jsx(FaClock, {}) }), _jsx(StatLabel, { children: "Amount Owed" }), _jsxs(StatValue, { children: ["$", statistics.totalOwed?.toLocaleString() || 0] })] })] }));
    // Actions Section
    const actionsSection = (_jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => setShowAddModal(true), "$primary": true, children: "+ Record Purchase" }), _jsx(Button, { onClick: fetchPurchases, disabled: loading, children: loading ? 'Loading...' : 'Refresh' })] }));
    // Filters Section
    const filtersSection = (_jsxs(FiltersContainer, { children: [_jsxs(FilterGroup, { children: [_jsx(Label, { children: "Supplier" }), _jsxs(Select, { value: supplierFilter, onChange: (e) => setSupplierFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Suppliers" }), suppliers.map(s => (_jsxs("option", { value: s.id, children: [s.supplierCode, " - ", s.companyName] }, s.id)))] })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Payment Status" }), _jsxs(Select, { value: statusFilter, onChange: (e) => setStatusFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Statuses" }), _jsx("option", { value: "unpaid", children: "Unpaid" }), _jsx("option", { value: "partial", children: "Partially Paid" }), _jsx("option", { value: "paid", children: "Paid" })] })] }), _jsxs(FilterGroup, { children: [_jsx(Label, { children: "Purchase Type" }), _jsxs(Select, { value: typeFilter, onChange: (e) => setTypeFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Types" }), _jsx("option", { value: "outright", children: "Outright" }), _jsx("option", { value: "memo", children: "On Memo" }), _jsx("option", { value: "consignment", children: "Consignment" })] })] }), _jsx(FilterGroup, { children: _jsx(Button, { onClick: fetchPurchases, disabled: loading, children: "Apply Filters" }) })] }));
    return (_jsxs(TabPageLayout, { title: "Purchase Management", subtitle: "Track purchases from suppliers and manage inventory costs (Level 5.3)", stats: statsSection, actions: actionsSection, filters: filtersSection, children: [_jsxs(TableContainer, { children: [_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(SortableTh, { sortKey: "purchaseNumber", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Purchase #" }), _jsx(SortableTh, { sortKey: "purchaseDate", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Date" }), _jsx(SortableTh, { sortKey: "supplier", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Supplier" }), _jsx(SortableTh, { sortKey: "purchaseType", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Type" }), _jsx(SortableTh, { sortKey: "items", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Items" }), _jsx(SortableTh, { sortKey: "total", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Total" }), _jsx(SortableTh, { sortKey: "amountPaid", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Paid" }), _jsx(SortableTh, { sortKey: "amountOwed", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Owed" }), _jsx(SortableTh, { sortKey: "paymentStatus", currentSortKey: sortKey, sortDirection: sortDirection, onSort: handleSort, children: "Status" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: sortedPurchases.map((purchase) => (_jsxs("tr", { children: [_jsx(Td, { children: _jsx(Code, { children: purchase.purchaseNumber }) }), _jsx(Td, { children: new Date(purchase.purchaseDate).toLocaleDateString() }), _jsxs(Td, { children: [_jsx("strong", { children: purchase.supplierCode }), _jsx("br", {}), _jsx("small", { children: purchase.supplierName })] }), _jsx(Td, { children: _jsx(Badge, { color: PURCHASE_TYPE_COLORS[purchase.purchaseType], children: PURCHASE_TYPE_LABELS[purchase.purchaseType] }) }), _jsxs(Td, { children: [purchase.items.length, " items"] }), _jsx(Td, { children: _jsxs("strong", { children: ["$", purchase.total.toLocaleString()] }) }), _jsxs(Td, { children: ["$", purchase.amountPaid.toLocaleString()] }), _jsxs(Td, { className: purchase.amountOwed > 0 ? 'owed' : '', children: ["$", purchase.amountOwed.toLocaleString()] }), _jsx(Td, { children: _jsx(Badge, { color: PAYMENT_STATUS_COLORS[purchase.paymentStatus], children: PAYMENT_STATUS_LABELS[purchase.paymentStatus] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsx(ActionButton, { onClick: () => viewPurchase(purchase.id), children: "View" }), purchase.amountOwed > 0 && (_jsx(ActionButton, { onClick: () => handleRecordPayment(purchase), style: { background: 'rgba(184, 134, 11, 0.1)', borderColor: '#B8860B', color: '#B8860B' }, children: "Pay" }))] }) })] }, purchase.id))) })] }), sortedPurchases.length === 0 && !loading && (_jsxs(EmptyState, { children: [_jsx(EmptyStateIcon, {}), _jsx("h3", { children: "No purchases found" }), _jsx(EmptyStateText, { children: "Click \"Record Purchase\" to add your first purchase from a supplier" })] }))] }), _jsxs(BaseModal, { isOpen: showAddModal, onClose: () => {
                    setShowAddModal(false);
                    resetForm();
                }, title: "Record New Purchase", maxWidth: "900px", footer: _jsxs(_Fragment, { children: [_jsx(Button, { onClick: () => {
                                setShowAddModal(false);
                                resetForm();
                            }, children: "Cancel" }), _jsx(Button, { onClick: createPurchase, "$primary": true, disabled: loading || !formData.supplierId || formData.items.length === 0, children: loading ? 'Creating...' : 'Create Purchase' })] }), children: [_jsxs(Section, { children: [_jsx(SectionTitle, { children: "Purchase Details" }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Supplier *" }), _jsxs(Select, { value: formData.supplierId, onChange: (e) => setFormData({ ...formData, supplierId: e.target.value }), children: [_jsx("option", { value: "", children: "Select Supplier" }), suppliers.map(s => (_jsxs("option", { value: s.id, children: [s.supplierCode, " - ", s.companyName] }, s.id)))] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Purchase Date *" }), _jsx(Input, { type: "date", value: formData.purchaseDate, onChange: (e) => setFormData({ ...formData, purchaseDate: e.target.value }) })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Purchase Type *" }), _jsxs(Select, { value: formData.purchaseType, onChange: (e) => setFormData({ ...formData, purchaseType: e.target.value }), children: [_jsx("option", { value: "outright", children: "Outright Purchase" }), _jsx("option", { value: "memo", children: "On Memo" }), _jsx("option", { value: "consignment", children: "Consignment" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Due Date" }), _jsx(Input, { type: "date", value: formData.dueDate, onChange: (e) => setFormData({ ...formData, dueDate: e.target.value }) })] })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Add Line Items (Diamonds)" }), _jsx("div", { style: { marginBottom: '1rem', padding: '1rem', background: 'rgba(2, 132, 199, 0.15)', borderLeft: '3px solid #0284c7', borderRadius: '6px' }, children: _jsx("div", { style: { fontSize: '0.875rem', color: '#38bdf8', fontWeight: 500 }, children: "Tip: Enter stock number and click \"Lookup Diamond\" to auto-fill from inventory" }) }), _jsxs(LineItemForm, { children: [_jsxs(FormGroup, { style: { gridColumn: '1 / -1' }, children: [_jsx(Label, { children: "Stock Number *" }), _jsxs("div", { style: { display: 'flex', gap: '0.5rem' }, children: [_jsx(Input, { type: "text", value: lineItem.stockNumber, onChange: (e) => setLineItem({ ...lineItem, stockNumber: e.target.value }), onKeyPress: (e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                lookupDiamond(lineItem.stockNumber);
                                                            }
                                                        }, placeholder: "ABC123 or OP20352", style: { flex: 1 } }), _jsx(Button, { onClick: () => lookupDiamond(lineItem.stockNumber), type: "button", disabled: !lineItem.stockNumber.trim(), style: { padding: '0.5rem 1.5rem', whiteSpace: 'nowrap' }, children: "Lookup Diamond" })] })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Description *" }), _jsx(Input, { type: "text", value: lineItem.description, onChange: (e) => setLineItem({ ...lineItem, description: e.target.value }), placeholder: "1.50ct Round H VS1" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Carat Weight *" }), _jsx(Input, { type: "number", step: "0.01", value: lineItem.caratWeight || '', onChange: (e) => setLineItem({ ...lineItem, caratWeight: parseFloat(e.target.value) || 0 }), placeholder: "1.50" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Cost/Carat *" }), _jsx(Input, { type: "number", step: "0.01", value: lineItem.costPerCarat || '', onChange: (e) => setLineItem({ ...lineItem, costPerCarat: parseFloat(e.target.value) || 0 }), placeholder: "5000" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Total Cost" }), _jsx(Input, { type: "text", value: `$${(lineItem.costPerCarat * lineItem.caratWeight * lineItem.quantity).toLocaleString()}`, disabled: true })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "\u00A0" }), _jsx(Button, { onClick: addLineItem, type: "button", children: "+ Add Item" })] })] }), formData.items.length > 0 && (_jsxs(LineItemsList, { children: [_jsxs(LineItemsTitle, { children: ["Items in Purchase (", formData.items.length, "):"] }), formData.items.map((item, index) => (_jsxs(LineItemRow, { children: [_jsxs(LineItemText, { children: [_jsx("strong", { children: item.stockNumber }), " - ", item.description, _jsx("br", {}), _jsxs("small", { children: [item.caratWeight, "ct \u00D7 $", item.costPerCarat.toLocaleString(), "/ct = $", item.totalCost.toLocaleString()] })] }), _jsx(RemoveButton, { onClick: () => removeLineItem(index), children: "\u00D7" })] }, index)))] }))] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Totals" }), _jsxs(FormGrid, { children: [_jsxs(FormGroup, { children: [_jsx(Label, { children: "Subtotal" }), _jsx(Input, { type: "text", value: `$${calculateSubtotal().toLocaleString()}`, disabled: true })] }), _jsxs(FormGroup, { children: [_jsxs(Label, { children: ["Tax Rate (%) ", _jsxs("small", { style: { color: '#666', fontWeight: 400 }, children: ["= $", calculateTaxAmount().toLocaleString()] })] }), _jsx(Input, { type: "number", step: "0.01", min: "0", max: "100", value: formData.taxRate || '', onChange: (e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) || 0 }), placeholder: "8.5" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Shipping" }), _jsx(Input, { type: "number", step: "0.01", value: formData.shippingCost || '', onChange: (e) => setFormData({ ...formData, shippingCost: parseFloat(e.target.value) || 0 }), placeholder: "0" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: "Discount" }), _jsx(Input, { type: "number", step: "0.01", value: formData.discountAmount || '', onChange: (e) => setFormData({ ...formData, discountAmount: parseFloat(e.target.value) || 0 }), placeholder: "0" })] }), _jsxs(FormGroup, { children: [_jsx(Label, { children: _jsx("strong", { children: "Total" }) }), _jsx(Input, { type: "text", value: `$${calculateTotal().toLocaleString()}`, disabled: true, style: { fontWeight: 'bold', fontSize: '1.1rem' } })] })] }), _jsxs(FormGroup, { fullWidth: true, children: [_jsx(Label, { children: "Notes" }), _jsx(Textarea, { value: formData.notes, onChange: (e) => setFormData({ ...formData, notes: e.target.value }), placeholder: "Additional notes about this purchase...", rows: 3 })] })] })] }), _jsx(BaseModal, { isOpen: !!selectedPurchase, onClose: () => setSelectedPurchase(null), title: `Purchase Details - ${selectedPurchase?.purchaseNumber || ''}`, maxWidth: "800px", footer: _jsx(Button, { onClick: () => setSelectedPurchase(null), children: "Close" }), children: selectedPurchase && (_jsxs(_Fragment, { children: [_jsxs(DetailGrid, { children: [_jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Supplier" }), _jsxs(DetailValue, { children: [selectedPurchase.supplierCode, " - ", selectedPurchase.supplierName] })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Purchase Date" }), _jsx(DetailValue, { children: new Date(selectedPurchase.purchaseDate).toLocaleDateString() })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Type" }), _jsx(DetailValue, { children: _jsx(Badge, { color: PURCHASE_TYPE_COLORS[selectedPurchase.purchaseType], children: PURCHASE_TYPE_LABELS[selectedPurchase.purchaseType] }) })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Payment Status" }), _jsx(DetailValue, { children: _jsx(Badge, { color: PAYMENT_STATUS_COLORS[selectedPurchase.paymentStatus], children: PAYMENT_STATUS_LABELS[selectedPurchase.paymentStatus] }) })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Subtotal" }), _jsxs(DetailValue, { children: ["$", selectedPurchase.subtotal.toLocaleString()] })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Total" }), _jsx(DetailValue, { children: _jsxs("strong", { children: ["$", selectedPurchase.total.toLocaleString()] }) })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Amount Paid" }), _jsxs(DetailValue, { style: { color: '#10b981' }, children: ["$", selectedPurchase.amountPaid.toLocaleString()] })] }), _jsxs(DetailItem, { children: [_jsx(DetailLabel, { children: "Amount Owed" }), _jsxs(DetailValue, { style: { color: '#ef4444' }, children: ["$", selectedPurchase.amountOwed.toLocaleString()] })] })] }), _jsxs(Section, { children: [_jsxs(SectionTitle, { children: ["Line Items (", selectedPurchase.items.length, ")"] }), _jsx(LineItemsList, { children: selectedPurchase.items.map((item, index) => (_jsxs(PurchaseLineItem, { children: [_jsxs(LineItemHeader, { children: [_jsx("strong", { children: item.stockNumber }), _jsxs(LineItemTotal, { children: ["$", item.totalCost.toLocaleString()] })] }), _jsxs(LineItemDetails, { children: [item.description, _jsx("br", {}), _jsxs("small", { children: [item.caratWeight, "ct \u00D7 $", item.costPerCarat.toLocaleString(), "/ct \u00D7 ", item.quantity, " = $", item.totalCost.toLocaleString()] })] })] }, index))) })] }), selectedPurchase.notes && (_jsxs(Section, { children: [_jsx(SectionTitle, { children: "Notes" }), _jsx(NotesText, { children: selectedPurchase.notes })] }))] })) }), _jsx(RecordPaymentModal, { isOpen: showPaymentModal, onClose: () => {
                    setShowPaymentModal(false);
                    setPaymentPurchase(null);
                }, purchase: paymentPurchase, onSuccess: handlePaymentSuccess })] }));
};
// Component-specific styled components (not in SharedComponents)
const Code = styled.code `
    background: var(--bg-tertiary);
    color: var(--text-primary);
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.8rem;
`;
const Badge = styled.span `
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    background: ${props => props.color || '#e5e7eb'}22;
    color: ${props => props.color || '#6b7280'};
    border: 1px solid ${props => props.color || '#6b7280'}44;
`;
const ActionButtons = styled.div `
    display: flex;
    gap: 0.5rem;
`;
const ActionButton = styled.button `
    padding: 0.375rem 0.75rem;
    border-radius: 4px;
    border: 1px solid ${props => props.danger ? 'rgba(239, 68, 68, 0.4)' : 'var(--border-color)'};
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.1)' : 'var(--bg-tertiary)'};
    color: ${props => props.danger ? '#ef4444' : 'var(--text-primary)'};
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${props => props.danger ? 'rgba(239, 68, 68, 0.2)' : 'var(--bg-secondary)'};
    }
`;
const Textarea = styled.textarea `
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    background: var(--bg-primary);
    color: var(--text-primary);
    resize: vertical;
    font-family: inherit;
    width: 100%;
    box-sizing: border-box;

    &::placeholder {
        color: var(--text-tertiary);
    }

    &:focus {
        outline: none;
        border-color: var(--accent-gold);
        box-shadow: 0 0 0 3px var(--accent-gold-bg);
    }
`;
const FormGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
`;
const FormGroup = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};
`;
const Section = styled.div `
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--border-color, #e5e7eb);

    &:last-child {
        border-bottom: none;
        margin-bottom: 0;
    }
`;
const SectionTitle = styled.h3 `
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--text-primary, #1a1a1a);
    margin: 0 0 1rem 0;
`;
const LineItemForm = styled.div `
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
    padding: 1rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;
const LineItemsList = styled.div `
    margin-top: 1rem;
`;
const LineItemsTitle = styled.div `
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary, #1a1a1a);
    margin-bottom: 0.75rem;
`;
const LineItemRow = styled.div `
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    margin-bottom: 0.5rem;
`;
const LineItemText = styled.div `
    flex: 1;
    font-size: 0.875rem;

    small {
        color: var(--text-secondary, #666);
    }
`;
const RemoveButton = styled.button `
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.4);
    color: #ef4444;
    border-radius: 4px;
    width: 28px;
    height: 28px;
    font-size: 1.2rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(239, 68, 68, 0.25);
    }
`;
const DetailGrid = styled.div `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;
`;
const DetailItem = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;
const DetailLabel = styled.div `
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--text-secondary, #666);
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;
const DetailValue = styled.div `
    font-size: 1rem;
    color: var(--text-primary, #1a1a1a);
`;
const PurchaseLineItem = styled.div `
    padding: 1rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    margin-bottom: 0.75rem;
`;
const LineItemHeader = styled.div `
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
`;
const LineItemTotal = styled.div `
    font-weight: 600;
    color: #3b82f6;
`;
const LineItemDetails = styled.div `
    font-size: 0.875rem;
    color: var(--text-secondary, #666);
`;
const NotesText = styled.div `
    padding: 1rem;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 0.875rem;
    color: var(--text-primary);
    white-space: pre-wrap;
`;
export default PurchaseManagement;
